import React from 'react';

import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { PricingPlanObjectFormSchemaValues } from 'src/features/pricing/sites/utils/formSchema';
import { useListPricingPlans } from 'src/features/pricingPlan/api/useListPricingPlans';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { PRICING_PLAN_STATUS } from 'src/features/pricingPlan/utils/types';
import { VISITOR_TYPE } from 'src/features/visitor/types';
import { visitorTypeBadgeOptions } from 'src/features/visitor/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';
import { FormFieldBlockTooltip } from 'src/registry/components/blocks/FormFieldBlock';
import MultiSelectFormField from 'src/registry/components/forms/MultiSelectFormField';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { Checkbox } from 'src/registry/ui/Checkbox';
import DatePickerWithTime from 'src/registry/ui/DatePickerWithTime';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';

type PricingPlanFormProps = {
    form: UseFormReturn<PricingPlanObjectFormSchemaValues>;
};

function ObjectPricingPlanForm({ form }: PricingPlanFormProps) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    // Standard (default) plans are reserved for copying only, so exclude them from selection here.
    // Only published plans can be assigned to an object; drafts are not selectable.
    const { data: pricingPlansData } = useListPricingPlans({
        currently_active: true,
        is_system: false,
        status: PRICING_PLAN_STATUS.PUBLISHED,
        parkingId,
    });

    const pricingPlanOptions = React.useMemo(() => {
        if (!pricingPlansData) return [];
        return pricingPlansData.results.map((plan) => {
            const translatedPlan = plan.translations.find((p) => p.language === locale);

            return { value: plan.identity, label: translatedPlan?.name || '' };
        });
    }, [pricingPlansData, locale]);

    // Nothing selected means the assignment prices every type of visitor, which is what an object priced the same
    // way whatever enters it carries - so the field is not a required one
    const visitorTypeOptions = React.useMemo(
        () =>
            Object.values(VISITOR_TYPE).map((visitorType) => ({
                id: visitorType,
                name: formatMessage(visitorTypeBadgeOptions[visitorType].label),
            })),
        [formatMessage],
    );

    return (
        <Form form={form} variant="dialog">
            <SelectFormField<PricingPlanObjectFormSchemaValues>
                form={form}
                name="pricing_plan_identity"
                label={formatMessage(pricingPlanMessages.pricingPlan)}
                options={pricingPlanOptions}
            />
            <MultiSelectFormField<PricingPlanObjectFormSchemaValues>
                form={form}
                name="visitor_types"
                required={false}
                label={formatMessage(pricingPlanMessages.visitorTypes)}
                placeholder={formatMessage(pricingPlanMessages.allVisitorTypes)}
                options={visitorTypeOptions}
            />
            <FormField
                control={form.control}
                name="valid_from"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(pricingPlanMessages.validFrom)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <DatePickerWithTime dateISO={field.value} onChange={field.onChange} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="valid_to"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(pricingPlanMessages.validTo)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <DatePickerWithTime dateISO={field.value ?? ''} onChange={field.onChange} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="is_exception"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...pricingPlanMessages.isException} />
                        </FormLabel>
                        <FormControlStack className="flex-row items-center gap-2">
                            <FormControl>
                                <Checkbox
                                    checked={!!field.value}
                                    onCheckedChange={(checked) => field.onChange(checked === true)}
                                />
                            </FormControl>
                            <FormFieldBlockTooltip className="flex min-h-18 flex-row items-center text-left sm:max-w-lg sm:gap-2">
                                <FontAwesomeIcon icon={faCircleQuestion} className="text-[14px] text-gray-600" />
                                <div className="text-sm text-gray-600">
                                    {formatMessage(pricingPlanMessages.isExceptionMoreInfo)}
                                </div>
                            </FormFieldBlockTooltip>
                        </FormControlStack>
                    </FormItem>
                )}
            />
        </Form>
    );
}

export default ObjectPricingPlanForm;
